// functions/home_page_functions.js

// Import query routes
import { fetch_report_data, fetch_route_report_data, fetch_driver_routes_data, fetch_fire_register_report_data, fetch_weekly_storage_report } from "../../api/route";
import { fetch_snapshot_data } from "../../api/warehouse";
import { search_purchase_orders_by_po, search_purchase_orders_by_date_range } from "../../api/purchase_orders";
import { fetch_vehicle_lease_report_data, fetch_active_vehicles_report, download_all_vehicles_audit, download_all_vehicles_files_zip, download_missing_docs_report } from '../../api/vehicles';
import { fetchInboundReports, fetchDispatchReports } from "../../api/enhancedWarehouse";
import { getSessionUUID } from "../../components/lib/AuthToken";

export class HomePageReports {
    constructor(showAlert) {
        this.showAlert = showAlert;
    }

    fetchReports = async (selectedDate, setMonthlyReportData, setShowReport, setIsLoading, resetReports) => {
        setIsLoading(true);
        try {
            const { data, status } = await fetch_report_data(selectedDate);
            if (status === 200) {
                setMonthlyReportData(data);
                setShowReport(true);
            } else {
                setMonthlyReportData([]);
                setShowReport(true);
            }
        } catch (error) {
            resetReports();
            this.showAlert({ message: 'An error occurred while fetching data.', type: 'danger', buttons: [{ type: 'danger', label: 'Close' }] });
        } finally {
            setIsLoading(false);
        }
    };

    fetchRouteReports = async ( reportQueryData, setReportTableData, setShowReport, setIsLoading, resetReports ) => {
        setIsLoading(true);
        try {
            const { data, status } = await fetch_route_report_data(reportQueryData);
            if (status === 200) {
                setReportTableData(data);
                setShowReport(true);
            } else {
                setReportTableData([]);
                setShowReport(true);
            }
        } catch (error) {
            resetReports();
            this.showAlert({
                message: 'An error occurred while fetching data.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setIsLoading(false);
        }
    };

    fetchSnapShot = async ( customerData, setSnapShotData, setShowReport, setIsLoading, resetReports ) => {
        setIsLoading(true);
        try {
            const { data, status, message } = await fetch_snapshot_data(customerData);
            
            if (status === 200) {
                setSnapShotData(data);
                setShowReport(true);
            } else {
                setSnapShotData([]);
                setShowReport(true);
                
                // Show specific error for authentication issues
                if (status === 401) {
                    this.showAlert({
                        message: 'Authentication failed. Please log out and log back in.',
                        type: 'danger',
                        buttons: [{ type: 'danger', label: 'Close' }],
                    });
                    return;
                }
            }
        } catch (error) {
            resetReports();
            this.showAlert({
                message: 'An error occurred while fetching data.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setIsLoading(false);
        }
    };

    fetchDriverRoutes = async (driverRouteData, setDriverRoutesData, setShowReport, setIsLoading, resetReports) => {
        setIsLoading(true);
        try {
            const { data, status } = await fetch_driver_routes_data(driverRouteData);
            if (status === 200) {
                setDriverRoutesData(data);
                setShowReport(true);
            } else {
                setDriverRoutesData([]);
                setShowReport(true);
            }
        } catch (error) {
            resetReports();
            this.showAlert({
                message: 'An error occurred while fetching driver routes data.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setIsLoading(false);
        }
    }

    fetchFireRegisterReport = async (weekData, setFireRegisterReportData, setShowReport, setIsLoading, resetReports) => {
        setIsLoading(true);
        try {
            const { data, status } = await fetch_fire_register_report_data(weekData);
            if (status === 200) {
                setFireRegisterReportData(data);
                setShowReport(true);
            } else {
                setFireRegisterReportData([]);
                setShowReport(true);
            }
        } catch (error) {
            resetReports();
            this.showAlert({
                message: 'An error occurred while fetching fire register report data.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setIsLoading(false);
        }
    }

    fetchWeeklyStorageReport = async (weekData, setWeeklyStorageData, setShowReport, setIsLoading, resetReports) => {
        setIsLoading(true);
        try {
            const { data, status } = await fetch_weekly_storage_report(weekData);
            if (status === 200) {
                setWeeklyStorageData(data);
                setShowReport(true);
            } else {
                setWeeklyStorageData([]);
                setShowReport(true);
            }
        } catch (error) {
            resetReports();
            this.showAlert({
                message: 'An error occurred while fetching weekly storage report data.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setIsLoading(false);
        }
    }

    fetchPOByNumber = async (poNumber, setPurchaseOrdersReportData, setShowReport, setIsLoading, resetReports) => {
        setIsLoading(true);
        try {
            const { data, status } = await search_purchase_orders_by_po(poNumber);
            if (status === 200 && data.success) {
                setPurchaseOrdersReportData(data.pos || []);
                setShowReport(true);
            } else {
                setPurchaseOrdersReportData([]);
                setShowReport(true);
            }
        } catch (error) {
            resetReports();
            this.showAlert({
                message: 'An error occurred while fetching purchase order data.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setIsLoading(false);
        }
    }

    fetchPOByDateRange = async (dateRange, setPurchaseOrdersReportData, setShowReport, setIsLoading, resetReports) => {
        setIsLoading(true);
        try {
            const { data, status } = await search_purchase_orders_by_date_range(dateRange);
            if (status === 200 && data.success) {
                setPurchaseOrdersReportData(data.pos || []);
                setShowReport(true);
            } else {
                setPurchaseOrdersReportData([]);
                setShowReport(true);
            }
        } catch (error) {
            resetReports();
            this.showAlert({
                message: 'An error occurred while fetching purchase order data.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setIsLoading(false);
        }
    }

    fetchVehicleLeaseReport = async (selectedDate, setVehicleLeaseReportData, setShowReport, setIsLoading, resetReports) => {
        setIsLoading(true);
        try {
            const { data, status } = await fetch_vehicle_lease_report_data(selectedDate);
            if (status === 200) {
                setVehicleLeaseReportData(data);
                setShowReport(true);
            } else {
                setVehicleLeaseReportData([]);
                setShowReport(true);
            }
        } catch (error) {
            resetReports();
            this.showAlert({
                message: 'An error occurred while fetching vehicle lease report data.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setIsLoading(false);
        }
    }

    fetchVehiclesRentalReport = async (dateRange, setActiveVehiclesData, setShowReport, setIsLoading, resetReports) => {
        setIsLoading(true);
        try {
            
            // Extract the actual dates
            const startDate = dateRange.startDate;
            const endDate = dateRange.endDate;
            
            const { data, status } = await fetch_active_vehicles_report(startDate, endDate);
            if (status === 200) {
                setActiveVehiclesData(data);
                setShowReport(true);
            } else {
                setActiveVehiclesData([]);
                setShowReport(true);
            }
        } catch (error) {
            resetReports();
            this.showAlert({
                message: 'An error occurred while fetching active vehicles report data.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setIsLoading(false);
        }
    }

    fetchInboundReports = async (dateRange, setInboundReportsData, setShowReport, setIsLoading, resetReports) => {
        setIsLoading(true);
        try {
            console.log("Fetching inbound reports for date range:", dateRange);
            // Pass null as customer filter for home page (admin view - all customers)
            const { data, status, success, message } = await fetchInboundReports(dateRange.startDate, dateRange.endDate, null);
            console.log("Inbound reports response:", { data, status, success, message });
            
            if (status === 200 && success) {
                console.log("Setting inbound reports data:", data);
                setInboundReportsData(data || []);
                setShowReport(true);
            } else {
                console.log("No data or error, setting empty array");
                setInboundReportsData([]);
                setShowReport(true);
            }
        } catch (error) {
            resetReports();
            this.showAlert({
                message: 'An error occurred while fetching inbound reports data.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setIsLoading(false);
        }
    }

    fetchDispatchReports = async (dateRange, setDispatchReportsData, setShowReport, setIsLoading, resetReports) => {
        setIsLoading(true);
        try {
            // Pass null as customer filter for home page (admin view - all customers)
            const { data, status, success, message } = await fetchDispatchReports(dateRange.startDate, dateRange.endDate, null);
            
            if (success && status === 200 && data) {
                console.log("Dispatch reports data received:", data);
                setDispatchReportsData(data || []);
                setShowReport(true);
            } else {
                console.log("No data or error, setting empty array");
                setDispatchReportsData([]);
                setShowReport(true);
            }
        } catch (error) {
            resetReports();
            this.showAlert({
                message: 'An error occurred while fetching dispatch reports data.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setIsLoading(false);
        }
    }

    fetchClockInOutReport = async (dateRange, setClockInOutReportData, setShowReport, setIsLoading, resetReports) => {
        setIsLoading(true);
        try {
            const sessionToken = await getSessionUUID();
            const response = await fetch('/timesheets/clock-events/range', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({
                    start_date: dateRange.startDate,
                    end_date: dateRange.endDate
                })
            });

            if (response.ok) {
                const result = await response.json();
                const data = result.data || [];
                console.log("Clock In/Out report data received:", data);
                setClockInOutReportData(data);
                setShowReport(true);
            } else {
                console.log("No data or error, setting empty array");
                setClockInOutReportData([]);
                setShowReport(true);
            }
        } catch (error) {
            resetReports();
            this.showAlert({
                message: 'An error occurred while fetching clock in/out report data.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setIsLoading(false);
        }
    }

    fetchTimeSheetReport = async (dateData, setTimeSheetReportData, setShowReport, setIsLoading, resetReports) => {
        setIsLoading(true);
        try {
            const sessionToken = await getSessionUUID();
            const response = await fetch('/timesheets/weekly-report', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({
                    date: dateData.date
                })
            });

            if (response.ok) {
                const result = await response.json();
                const data = result.data || [];
                console.log("Time Sheet report data received:", data);
                setTimeSheetReportData(data);
                setShowReport(true);
            } else {
                console.log("No data or error, setting empty array");
                setTimeSheetReportData([]);
                setShowReport(true);
            }
        } catch (error) {
            resetReports();
            this.showAlert({
                message: 'An error occurred while fetching time sheet report data.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setIsLoading(false);
        }
    }

    updateTimesheetComments = async (timesheetId, comments) => {
        try {
            const sessionToken = await getSessionUUID();
            const response = await fetch('/timesheets/update-comments', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({
                    timesheet_id: timesheetId,
                    comments: comments
                })
            });

            if (!response.ok) {
                throw new Error('Failed to update comments');
            }

            return await response.json();
        } catch (error) {
            console.error('Error updating timesheet comments:', error);
            throw error;
        }
    }

    fetchAlcoholTestingReport = async (dateRange, setAlcoholTestingReportData, setShowReport, setIsLoading, resetReports) => {
        setIsLoading(true);
        try {
            const sessionToken = await getSessionUUID();
            const response = await fetch('/alcohol_testing/get_test_history?' + new URLSearchParams({
                start_date: dateRange.startDate,
                end_date: dateRange.endDate,
                limit: 1000
            }), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                }
            });

            if (response.ok) {
                const result = await response.json();
                const data = result.tests || [];
                console.log("Alcohol testing report data received:", data);
                setAlcoholTestingReportData(data);
                setShowReport(true);
            } else {
                console.log("No data or error, setting empty array");
                setAlcoholTestingReportData([]);
                setShowReport(true);
            }
        } catch (error) {
            resetReports();
            this.showAlert({
                message: 'An error occurred while fetching alcohol testing report data.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setIsLoading(false);
        }
    }

    // ...other methods...

    downloadAllVehiclesAudit = async (setIsLoading) => {
        setIsLoading(true);
        try {
            const result = await download_all_vehicles_audit();
            if (result.status !== 200) {
                this.showAlert({
                    message: result.message || 'Failed to download all vehicles audit',
                    type: 'danger',
                    buttons: [{ type: 'danger', label: 'Close' }],
                });
            }
        } catch (error) {
            this.showAlert({
                message: 'An error occurred while generating the audit.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setIsLoading(false);
        }
    };

    downloadAllVehiclesFilesZip = async (setIsDownloading, onProgress) => {
        setIsDownloading(true);
        try {
            const result = await download_all_vehicles_files_zip(onProgress);
            if (result.status !== 200) {
                this.showAlert({
                    message: result.message || 'Failed to download all vehicle files',
                    type: 'danger',
                    buttons: [{ type: 'danger', label: 'Close' }],
                });
            }
            return result;
        } catch (error) {
            this.showAlert({
                message: 'An error occurred while generating the zip.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
            return { status: 500, message: error.message };
        } finally {
            setIsDownloading(false);
        }
    };

    downloadMissingDocsReport = async (setIsLoading) => {
        setIsLoading(true);
        try {
            const result = await download_missing_docs_report();
            if (result.status !== 200) {
                this.showAlert({
                    message: result.message || 'Failed to generate missing documents report',
                    type: 'danger',
                    buttons: [{ type: 'danger', label: 'Close' }],
                });
            }
        } catch (error) {
            this.showAlert({
                message: 'An error occurred while generating the report.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setIsLoading(false);
        }
    };
}